/**
 * Validation Rules Configuration
 *
 * Defines validation rules for OrgUnit and Location CSV imports.
 * Includes field-level validators, required field flags, and enum constants.
 *
 * @module ImportExportTab/utils/validationRules
 */

import { TIMEZONES, OWNERSHIP_TYPES as OWNERSHIP_TYPES_CONSTANT } from '../constants';

/**
 * Valid organization level enum values
 * @constant {string[]}
 */
export const ORG_LEVEL_ENUM = ['ORGLEVEL_1', 'ORGLEVEL_2', 'ORGLEVEL_3'];

/**
 * Valid timezone values (imported from constants)
 * @constant {string[]}
 */
export const TIMEZONE_LIST = TIMEZONES;

/**
 * Valid ownership types (imported from constants)
 * @constant {string[]}
 */
export const OWNERSHIP_TYPES = OWNERSHIP_TYPES_CONSTANT;

/**
 * Validation rules for OrgUnit CSV fields
 *
 * Each field has:
 * - required: boolean indicating if field is required
 * - validator: function to validate field value
 * - errorMessage: message to show on validation failure
 *
 * @constant {Object}
 */
export const ORGUNIT_RULES = {
  name: {
    required: true,
    validator: (value) => typeof value === 'string' && value.trim().length > 0,
    errorMessage: 'Name is required and must not be empty'
  },
  orgLevel: {
    required: true,
    validator: (value) => isValidEnum(value, ORG_LEVEL_ENUM),
    errorMessage: `Org Level must be one of: ${ORG_LEVEL_ENUM.join(', ')}`
  },
  parentOrgUnitName: {
    required: false,
    validator: (value) => !value || (typeof value === 'string' && value.trim().length > 0),
    errorMessage: 'Parent OrgUnit Name must be a valid string if provided'
  },
  isActive: {
    required: true,
    validator: isBoolean,
    errorMessage: 'isActive must be TRUE or FALSE'
  }
};

/**
 * Validation rules for Location CSV fields
 *
 * @constant {Object}
 */
export const LOCATION_RULES = {
  locationName: {
    required: true,
    validator: (value) => typeof value === 'string' && value.trim().length > 0,
    errorMessage: 'Location Name is required and must not be empty'
  },
  orgLevel_1_name: {
    required: false,
    validator: (value) => !value || (typeof value === 'string' && value.trim().length > 0),
    errorMessage: 'OrgLevel 1 Name must be a valid string if provided'
  },
  street_1: {
    required: true,
    validator: (value) => typeof value === 'string' && value.trim().length > 0,
    errorMessage: 'Street Address (street_1) is required and must not be empty'
  },
  street_2: {
    required: false,
    validator: (value) => !value || (typeof value === 'string'),
    errorMessage: 'Street Address 2 must be a valid string if provided'
  },
  city: {
    required: true,
    validator: (value) => typeof value === 'string' && value.trim().length > 0,
    errorMessage: 'City is required and must not be empty'
  },
  state: {
    required: true,
    validator: isValidState,
    errorMessage: 'State must be a valid 2-character state code (e.g., CA, NY, TX)'
  },
  zipCode: {
    required: true,
    validator: isValidZipCode,
    errorMessage: 'Zip Code must be in format XXXXX or XXXXX-XXXX'
  },
  timeZone: {
    required: true,
    validator: isValidTimezone,
    errorMessage: `Timezone must be one of: ${TIMEZONE_LIST.slice(0, 5).join(', ')}, ...`
  },
  ownershipType: {
    required: false,
    validator: (value) => !value || isValidOwnershipType(value),
    errorMessage: `Ownership Type must be one of: ${OWNERSHIP_TYPES.join(', ')}`
  }
};

/**
 * Validate if value is a boolean string (TRUE or FALSE, case insensitive)
 *
 * @param {*} value - Value to validate
 * @returns {boolean} True if valid boolean string
 *
 * @example
 * isBoolean('TRUE') // true
 * isBoolean('false') // true
 * isBoolean('yes') // false
 */
export function isBoolean(value) {
  if (value === undefined || value === null) {
    return false;
  }

  const normalized = String(value).toUpperCase().trim();
  return normalized === 'TRUE' || normalized === 'FALSE';
}

/**
 * Validate if value is a valid date string
 *
 * Accepts ISO format (YYYY-MM-DD) and US format (MM/DD/YYYY)
 *
 * @param {*} value - Value to validate
 * @returns {boolean} True if valid date string
 *
 * @example
 * isValidDate('2025-10-30') // true
 * isValidDate('10/30/2025') // true
 * isValidDate('invalid') // false
 */
export function isValidDate(value) {
  if (value === undefined || value === null || value === '') {
    return false;
  }

  const dateStr = String(value).trim();
  const date = new Date(dateStr);

  // Check if date is valid (not NaN)
  return !isNaN(date.getTime());
}

/**
 * Validate if value is in the provided enum array
 *
 * @param {*} value - Value to validate
 * @param {string[]} enumArray - Array of valid enum values
 * @returns {boolean} True if value is in enum array
 *
 * @example
 * isValidEnum('ORGLEVEL_1', ORG_LEVEL_ENUM) // true
 * isValidEnum('ORGLEVEL_4', ORG_LEVEL_ENUM) // false
 */
export function isValidEnum(value, enumArray) {
  if (value === undefined || value === null) {
    return false;
  }

  return enumArray.includes(String(value));
}

/**
 * Validate if value is a valid US state code (2 characters)
 *
 * @param {*} value - Value to validate
 * @returns {boolean} True if valid state code
 *
 * @example
 * isValidState('CA') // true
 * isValidState('ca') // true
 * isValidState('California') // false
 */
export function isValidState(value) {
  if (value === undefined || value === null || value === '') {
    return false;
  }

  const stateStr = String(value).trim();
  return /^[A-Za-z]{2}$/.test(stateStr);
}

/**
 * Validate if value is a valid US zip code
 *
 * Accepts 5-digit format (XXXXX) or ZIP+4 format (XXXXX-XXXX)
 *
 * @param {*} value - Value to validate
 * @returns {boolean} True if valid zip code
 *
 * @example
 * isValidZipCode('12345') // true
 * isValidZipCode('12345-6789') // true
 * isValidZipCode('1234') // false
 */
export function isValidZipCode(value) {
  if (value === undefined || value === null || value === '') {
    return false;
  }

  const zipStr = String(value).trim();
  return /^\d{5}(-\d{4})?$/.test(zipStr);
}

/**
 * Validate if value is a valid timezone from TIMEZONE_LIST
 *
 * @param {*} value - Value to validate
 * @returns {boolean} True if valid timezone
 *
 * @example
 * isValidTimezone('America/New_York') // true
 * isValidTimezone('EST') // false
 */
export function isValidTimezone(value) {
  if (value === undefined || value === null || value === '') {
    return false;
  }

  return TIMEZONE_LIST.includes(String(value));
}

/**
 * Validate if value is a valid ownership type
 *
 * @param {*} value - Value to validate
 * @returns {boolean} True if valid ownership type
 *
 * @example
 * isValidOwnershipType('Company') // true
 * isValidOwnershipType('Franchise') // true
 * isValidOwnershipType('Other') // false
 */
export function isValidOwnershipType(value) {
  if (value === undefined || value === null || value === '') {
    return false;
  }

  return OWNERSHIP_TYPES.includes(String(value));
}
